import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink } from 'lucide-react';
import { PortalDsButton, PortalDsEmptyState, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { Client } from '../../types/client.types';
import { StudioDashboardBlock } from './studio-dashboard-block';
import {
  STUDIO_BLOCK_TYPES,
  STUDIO_DEFAULT_ACCOUNT_ID,
  STUDIO_METRICS,
  clampStudioSpan,
  createDefaultStudioBlocks,
  createStudioBlockId,
  defaultSpanFor,
  formatStudioPlatformName,
} from './studio-dashboard.constants';
import type { StudioAccountOption, StudioBlock, StudioBlockType, StudioDashboard } from './studio-dashboard.types';

interface ManageClientStudioDashboardProps {
  client: Client;
  headerActionsEl?: HTMLElement | null;
  onPreview?: () => void;
}

function createDashboard(
  name: string,
  accountId: string,
  withDefaults: boolean
): StudioDashboard {
  return {
    id: createStudioBlockId(),
    name,
    accountId,
    blocks: withDefaults ? createDefaultStudioBlocks() : [],
  };
}

function getStudioAccountOptions(client: Client): StudioAccountOption[] {
  const options: StudioAccountOption[] = [
    { id: STUDIO_DEFAULT_ACCOUNT_ID, label: 'Default' },
  ];
  const seen = new Set<string>([STUDIO_DEFAULT_ACCOUNT_ID]);

  (client.views ?? []).forEach((view) => {
    const accountId = view.account_ext_id;
    if (!accountId || seen.has(accountId)) {
      return;
    }
    seen.add(accountId);
    options.push({
      id: accountId,
      label: `${view.account_name} (${formatStudioPlatformName(view.platform)})`,
    });
  });

  (client.access ?? []).forEach((access) => {
    if (!access.account || seen.has(access.account)) {
      return;
    }
    seen.add(access.account);
    options.push({
      id: access.account,
      label: `${access.account} (${formatStudioPlatformName(access.source)})`,
    });
  });

  return options;
}

export function ManageClientStudioDashboard({
  client,
  headerActionsEl,
  onPreview,
}: ManageClientStudioDashboardProps) {
  const accountOptions = useMemo(() => getStudioAccountOptions(client), [client]);
  const [dashboards, setDashboards] = useState<StudioDashboard[]>(() => (
    getStudioAccountOptions(client).map((option) => createDashboard(
      option.label,
      option.id,
      option.id === STUDIO_DEFAULT_ACCOUNT_ID
    ))
  ));
  const [activeAccountId, setActiveAccountId] = useState(STUDIO_DEFAULT_ACCOUNT_ID);
  const dragBlockId = useRef<string | null>(null);

  useEffect(() => {
    setDashboards((current) => {
      const byAccount = new Map(current.map((dashboard) => [dashboard.accountId, dashboard]));
      const knownIds = new Set(accountOptions.map((option) => option.id));
      const fromAccounts = accountOptions.map((option) => {
        const existing = byAccount.get(option.id);
        if (existing) {
          return { ...existing, name: option.label };
        }
        return createDashboard(
          option.label,
          option.id,
          option.id === STUDIO_DEFAULT_ACCOUNT_ID
        );
      });
      const extras = current.filter((dashboard) => !knownIds.has(dashboard.accountId));
      return [...fromAccounts, ...extras];
    });
  }, [accountOptions]);

  const activeDashboard = dashboards.find((dashboard) => dashboard.accountId === activeAccountId)
    ?? dashboards[0];

  const updateActiveBlocks = (mutator: (blocks: StudioBlock[]) => StudioBlock[]) => {
    if (!activeDashboard) {
      return;
    }
    setDashboards((current) => current.map((dashboard) => (
      dashboard.id === activeDashboard.id
        ? { ...dashboard, blocks: mutator(dashboard.blocks) }
        : dashboard
    )));
  };

  const addDashboard = () => {
    const customCount = dashboards.filter((dashboard) => (
      !accountOptions.some((option) => option.id === dashboard.accountId)
    )).length;
    const next = createDashboard(`Dashboard ${customCount + 2}`, createStudioBlockId(), false);
    setDashboards((current) => [...current, next]);
    setActiveAccountId(next.accountId);
  };

  const selectorItems = useMemo(() => {
    const knownIds = new Set(accountOptions.map((option) => option.id));
    const extras = dashboards
      .filter((dashboard) => !knownIds.has(dashboard.accountId))
      .map((dashboard) => ({ id: dashboard.accountId, label: dashboard.name }));
    return [...accountOptions, ...extras];
  }, [accountOptions, dashboards]);

  const usedMetricIds = useMemo(
    () => new Set(
      (activeDashboard?.blocks ?? [])
        .filter((block) => block.type === 'metric')
        .map((block) => block.metricId)
    ),
    [activeDashboard?.blocks]
  );
  const usedTypes = useMemo(
    () => new Set(
      (activeDashboard?.blocks ?? [])
        .filter((block) => block.type !== 'metric')
        .map((block) => block.type)
    ),
    [activeDashboard?.blocks]
  );

  const availableMetrics = STUDIO_METRICS.filter((metric) => !usedMetricIds.has(metric.id));
  const availableTypes = STUDIO_BLOCK_TYPES.filter((item) => !usedTypes.has(item.type));

  const addBlock = (type: StudioBlockType, metricId?: string) => {
    updateActiveBlocks((blocks) => [
      ...blocks,
      {
        id: createStudioBlockId(),
        type,
        span: defaultSpanFor(type),
        metricId,
      },
    ]);
  };

  const setSpan = (blockId: string, span: number) => {
    const nextSpan = clampStudioSpan(span);
    updateActiveBlocks((blocks) => blocks.map((block) => (
      block.id === blockId ? { ...block, span: nextSpan } : block
    )));
  };

  const removeBlock = (blockId: string) => {
    updateActiveBlocks((blocks) => blocks.filter((block) => block.id !== blockId));
  };

  const reorderBlocks = (targetId: string) => {
    const fromId = dragBlockId.current;
    dragBlockId.current = null;
    if (!fromId || fromId === targetId) {
      return;
    }
    updateActiveBlocks((blocks) => {
      const next = [...blocks];
      const from = next.findIndex((block) => block.id === fromId);
      const to = next.findIndex((block) => block.id === targetId);
      if (from < 0 || to < 0) {
        return blocks;
      }
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const headerActions = (
    <>
      <PortalDsButton
        variant="secondary"
        size="sm"
        onClick={addDashboard}
      >
        + New dashboard
      </PortalDsButton>
      <PortalDsButton
        variant="secondary"
        size="sm"
        onClick={onPreview}
      >
        <ExternalLink className="size-3.5" />
        Preview
      </PortalDsButton>
    </>
  );

  return (
    <div className="flex flex-col gap-5">
      {headerActionsEl ? createPortal(headerActions, headerActionsEl) : (
        <div className="flex justify-end gap-2">
          {headerActions}
        </div>
      )}

      <p className={portalDs.bodySm}>
        Choose an account to edit its Studio dashboard. Default applies when an account has no layout of its own.
      </p>

      <div className="flex flex-col gap-8 md:flex-row">
        <div className="w-full shrink-0 md:w-[220px]">
          <h3 className={cn(portalDs.headingH3, 'mb-3')}>
            Accounts
          </h3>
          <div className="space-y-1">
            {selectorItems.map((item) => {
              const isSelected = activeAccountId === item.id;
              const dashboard = dashboards.find((entry) => entry.accountId === item.id);
              const isEmpty = (dashboard?.blocks.length ?? 0) === 0
                && item.id !== STUDIO_DEFAULT_ACCOUNT_ID;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveAccountId(item.id)}
                  className={`flex w-full items-center justify-between rounded-lg px-3.5 py-3 text-left text-[13.5px] ${
                    isSelected
                      ? 'bg-[#EFF6FF] font-semibold text-[#2563EB]'
                      : 'text-[#0D0F1A] hover:bg-[#F0F1F5]'
                  }`}
                >
                  <span className={isEmpty ? 'opacity-40' : ''}>
                    {item.label}
                  </span>
                  {isSelected ? (
                    <span className="text-[#2563EB]">
                      ›
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid min-w-0 flex-1 grid-cols-1 items-start gap-5 lg:grid-cols-[230px_minmax(0,1fr)]">
          <div className="sticky top-0 flex flex-col gap-4">
            <div className={portalDs.headingH3}>
              Toolbox
            </div>

            <div>
              <div className={cn(portalDs.sectionLabel, 'mb-2')}>
                Metric cards
              </div>
              <div className="flex max-h-[220px] flex-col gap-1.5 overflow-y-auto">
                {availableMetrics.length === 0 ? (
                  <p className={portalDs.caption}>
                    All metric cards are on the canvas.
                  </p>
                ) : (
                  availableMetrics.map((metric) => (
                    <div
                      key={metric.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-[#EDEEF2] px-2.5 py-1.5"
                    >
                      <div className={cn(portalDs.bodySm, 'min-w-0 truncate')}>
                        {metric.label}
                      </div>
                      <PortalDsButton
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => addBlock('metric', metric.id)}
                      >
                        Add
                      </PortalDsButton>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div>
              <div className={cn(portalDs.sectionLabel, 'mb-2')}>
                Charts & tables
              </div>
              <div className="flex flex-col gap-1.5">
                {availableTypes.length === 0 ? (
                  <p className={portalDs.caption}>
                    All chart and table blocks are on the canvas.
                  </p>
                ) : (
                  availableTypes.map((item) => (
                    <div
                      key={item.type}
                      className="flex items-center justify-between gap-2 rounded-lg border border-[#EDEEF2] px-2.5 py-1.5"
                    >
                      <div className={cn(portalDs.bodySm, 'min-w-0 truncate')}>
                        {item.label}
                      </div>
                      <PortalDsButton
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => addBlock(item.type)}
                      >
                        Add
                      </PortalDsButton>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="min-w-0 overflow-x-auto">
            {activeAccountId !== STUDIO_DEFAULT_ACCOUNT_ID
              && (activeDashboard?.blocks.length ?? 0) === 0 ? (
                <p className={cn(portalDs.bodySm, 'mb-3')}>
                  No layout for this account yet — Default will apply until you add blocks.
                </p>
              ) : null}
            <div className="grid min-h-[200px] min-w-[720px] grid-cols-4 items-start gap-3.5">
              {(activeDashboard?.blocks.length ?? 0) === 0 ? (
                <div className="col-span-4">
                  <PortalDsEmptyState
                    title="This dashboard is empty"
                    description="Add metric cards, charts, and tables from the toolbox to build this client's dashboard."
                  />
                </div>
              ) : (
                activeDashboard.blocks.map((block) => (
                  <StudioDashboardBlock
                    key={block.id}
                    block={block}
                    onIncreaseSpan={() => setSpan(block.id, block.span + 1)}
                    onDecreaseSpan={() => setSpan(block.id, block.span - 1)}
                    onRemove={() => removeBlock(block.id)}
                    onDragStart={() => {
                      dragBlockId.current = block.id;
                    }}
                    onDrop={() => reorderBlocks(block.id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageClientStudioDashboard;
